/**
 * Friendly empty/error state when BeaCon setup (AWS account / credentials) is incomplete.
 * Primary CTA always points at Company Admin — AWS connectivity is Owner/Admin-only.
 */
import { Link } from 'react-router-dom'
import { Settings } from 'lucide-react'
import type { ApiCta } from '@/utils/apiErrors'

interface SetupRequiredStateProps {
  title?: string
  message: string
  cta?: ApiCta
  /** Optional secondary link (omit for AWS setup gaps). */
  secondaryCta?: ApiCta | null
}

export function SetupRequiredState({
  title = 'Finish setup to continue',
  message,
  cta = { path: '/beacon/admin', label: 'Open Company Admin' },
  secondaryCta = null,
}: SetupRequiredStateProps) {
  return (
    <div className="rounded-xl border border-dashed border-amber-300/80 bg-amber-50/60 px-6 py-10 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-beacon-orange shadow-sm">
        <Settings className="h-5 w-5" />
      </div>
      <h3 className="font-display text-lg text-beacon-navy">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">{message}</p>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
        <Link
          to={cta.path}
          className="inline-flex items-center rounded-lg bg-beacon-orange px-4 py-2 text-sm font-semibold text-beacon-navy hover:bg-beacon-orange/90"
        >
          {cta.label}
        </Link>
        {secondaryCta ? (
          <Link
            to={secondaryCta.path}
            className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            {secondaryCta.label}
          </Link>
        ) : null}
      </div>
    </div>
  )
}
